import { CommonModule } from '@angular/common';
import { Component, ElementRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { ContactType } from '@app/contacts/models/contact';
import { ImportErrorResponse } from '@app/contacts/models/contact';
import { ContactService } from '@app/contacts/services/contact.service';
import { BrandingField, defaultBrandingSettings } from '@app/core/models/branding.models';
import { SnackBarType } from '@app/core/models/snack-bar-data.model';
import { AppConfigService } from '@app/core/services/app-config.service';
import { AppTranslateService } from '@app/core/services/app-translate.service';
import { SnackbarService } from '@app/core/services/snack-bar.service';
import { BrandingColorDirective } from '@app/shared/directives/branding-color.directive';
import { ContactTypeLabelPipe } from '@app/shared/pipes/contact-filter-labels.pipe';
import { TranslateModule } from '@ngx-translate/core';
import { finalize, firstValueFrom } from 'rxjs';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatIconModule,
    TranslateModule,
    BrandingColorDirective,
    ContactTypeLabelPipe,
    MatDividerModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    MatInputModule,
  ],
  selector: 'app-import-contacts',
  templateUrl: './import-contacts.component.html',
  styleUrls: ['./import-contacts.component.scss'],
})
export class ImportContactsComponent {
  contactFile: File = new File([], '');
  importContactFormGroup: FormGroup;
  allowedFiles: string[] = ['.csv'];
  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;
  loading: boolean = false;
  protected readonly ContactType = ContactType;
  protected readonly BrandingField = BrandingField;
  protected defaultBrandingSettings = defaultBrandingSettings;

  constructor(
    private dialogRef: MatDialogRef<ImportContactsComponent>,
    private fb: FormBuilder,
    protected appConfigService: AppConfigService,
    private contactService: ContactService,
    private snackbar: SnackbarService,
    private translate: AppTranslateService
  ) {
    this.importContactFormGroup = this.fb.group({
      file: [null, Validators.required],
      type: [ContactType.Personal, Validators.required],
      merge: [1, Validators.required],
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  openFileSelector(): void {
    this.fileInput.nativeElement.click();
  }

  removeFaxFile(): void {
    this.contactFile = new File([], '');
    this.importContactFormGroup.controls['file'].reset();
  }

  addFile(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target.files) {
      this.contactFile = target.files[0];
      this.importContactFormGroup.controls['file'].patchValue(this.contactFile);
    }
    target.value = '';
  }

  async submit() {
    this.loading = true;
    if (this.contactFile.type !== 'text/csv') {
      this.snackbar.open(this.translate.instant('CONTACT_PREFERENCES.IF_EXPORT_DESC'), '', {
        duration: 5000,
        panelClass: SnackBarType.ERROR,
      });
      this.loading = false;
      return;
    }
    const formData = new FormData();
    formData.append('file', this.contactFile);
    formData.append('type', this.importContactFormGroup.value.type);
    formData.append('source', 'ucws');
    formData.append('merge', this.importContactFormGroup.value.merge);
    try {
      const response = await firstValueFrom(
        this.contactService.importContacts(formData).pipe(finalize(() => (this.loading = false)))
      );
      if (!Array.isArray(response)) {
        const errorResponse = response as ImportErrorResponse;
        if (errorResponse.errors?.includes('The following csv file headers are not allowed:')) {
          this.snackbar.open(
            `${this.translate.instant('CONTACT_PREFERENCES.CSV_HEADER_ERROR')} ${this.translate.instant('CONTACT_PREFERENCES.CSV_HEADER_ERROR_HELP')}`,
            this.translate.instant('CONTACT_PREFERENCES.OK'),
            {
              panelClass: SnackBarType.ERROR,
            }
          );
        }
      } else {
        /* filter failed imported contacts */
        const failedRows = response.filter((row) => !Array.isArray(row) && !row.success);
        /* show success message if all contacts are imported successfully, otherwise show error message with failed rows details */
        if (failedRows.length === 0) {
          this.snackbar.open(
            this.translate.instant('CONTACT_PREFERENCES.CONTACTS_IMPORT_SUCCESSFUL'),
            this.translate.instant('CONTACT_PREFERENCES.OK')
          );
        } else {
          const failedMessage = failedRows
            .map((row) => {
              const name = row.row_data.full_name || `${row.row_data.first_name} ${row.row_data.last_name}`.trim();
              const contact = [name, row.row_data.company].filter(Boolean).join(' - ');
              const errors = Array.isArray(row.errors) ? row.errors.join(', ') : row.errors;
              return `\n Row ${row.row_number}${contact ? ` (${contact})` : ''}: ${errors}`;
            })
            .join('');
          this.snackbar.open(
            this.translate.instant('CONTACT_PREFERENCES.CONTACTS_IMPORT_NOT_SUCCESSFULLY', { error: failedMessage }),
            this.translate.instant('CONTACT_PREFERENCES.OK'),
            {
              panelClass: ['error', 'import-contacts-error'],
              duration: 0,
            }
          );
        }
      }
      this.dialogRef.close();
    } catch {
      this.snackbar.open(this.translate.instant('CONTACT_PREFERENCES.CONTACTS_IMPORT_NOT_SUCCESSFUL'), '', {
        duration: 5000,
        panelClass: SnackBarType.ERROR,
      });
    }
  }
}
